/**
 * O caminho de volta das ligações da API.
 *
 * `server/api/bindings.ts` traduz **corpo → argumentos**, porque é o servidor que recebe
 * JSON e precisa chamar o caso de uso. Aqui é o contrário: a tela chama
 * `container.getProject.execute(projectId, session)` como sempre chamou, e alguém precisa
 * transformar esses argumentos no corpo que a ligação do outro lado espera desmontar.
 *
 * **São dois arquivos que precisam concordar, e concordar não pode depender de memória.**
 * O `call-shapes.test.ts` monta cada forma daqui, manda pela ligação correspondente de lá
 * e confere que os argumentos chegam iguais aos que saíram — se alguém acrescentar uma
 * sexta forma num lado só, o teste cai.
 *
 * ## A sessão some no caminho, e é isso que se quer
 *
 * Toda chamada da tela leva `session` como último argumento — era assim que o caso de uso
 * local sabia quem perguntava. Sobre HTTP, quem responde a essa pergunta é o **token**, no
 * cabeçalho: a sessão que vale é a que o servidor resolve, não a que o cliente afirma ter.
 * Mandar o objeto de sessão no corpo seria oferecer a quem chama a chance de dizer quem é
 * — que é exatamente a fronteira que a Fase 12 existe para mover para o servidor (RN-134).
 *
 * Então as formas abaixo **descartam** o argumento de sessão. Ele continua na assinatura
 * porque a tela não muda; ele só não viaja.
 */

/**
 * As seis formas, espelhando `BINDINGS`.
 *
 * O nome de cada uma é o da ligação do servidor, e de propósito: procurar `withField` no
 * repositório precisa achar os dois lados.
 */
export type CallShape =
  /** `execute(input, session)` — a grande maioria. O corpo é o próprio `input`. */
  | { readonly kind: 'withInput' }
  /** `execute(input)` — a entrada no sistema, que ainda não tem sujeito. */
  | { readonly kind: 'withInputOnly' }
  /** `execute(session)`, com um segundo argumento opcional que vira campo do corpo. */
  | { readonly kind: 'withSessionFirst'; readonly extra?: string }
  /** `execute(valor, session)` — um escalar, que vira um campo com este nome. */
  | { readonly kind: 'withField'; readonly name: string }
  /** `execute(a, b, session)` — dois escalares, dois campos. */
  | { readonly kind: 'withFields'; readonly first: string; readonly second: string }
  /** `execute()` — catálogo global: nada a mandar. */
  | { readonly kind: 'withNothing' }
  /** `execute({ ...corpo, bytes }, session)` — a planilha, que vira base64. */
  | { readonly kind: 'withBytes' };

/**
 * Os argumentos da tela viram o corpo da requisição.
 *
 * Recebe `args` como o `execute` os receberia, **inclusive a sessão**, e devolve só o que
 * atravessa a rede.
 */
export function bodyFor(shape: CallShape, args: readonly unknown[]): unknown {
  switch (shape.kind) {
    case 'withInput':
    case 'withInputOnly':
      return args[0] ?? {};

    case 'withSessionFirst':
      // A sessão é o primeiro argumento aqui, e o segundo é o que interessa.
      return shape.extra === undefined ? {} : { [shape.extra]: args[1] };

    case 'withField':
      return { [shape.name]: args[0] };

    case 'withFields':
      return { [shape.first]: args[0], [shape.second]: args[1] };

    case 'withNothing':
      return {};

    case 'withBytes': {
      // A planilha chega como `{ ...campos, bytes }` e sai como `{ ...campos,
      // bytesBase64 }` — o mesmo nome que `withBytes` procura do outro lado. O
      // `bytes` original não vai junto: `JSON.stringify` de um `Uint8Array` produz um
      // objeto de índices numerados, que atravessaria a rede dezenas de vezes maior e
      // chegaria irreconhecível.
      const { bytes, ...resto } = (args[0] ?? {}) as { bytes?: Uint8Array };
      return { ...resto, bytesBase64: encodeBase64(bytes ?? new Uint8Array(0)) };
    }
  }
}

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/**
 * Bytes para base64, sem depender do ambiente.
 *
 * `btoa` só existe no navegador e `Buffer` só no Node, e este arquivo é exercitado nos
 * dois — o teste roda fora de qualquer navegador. É o gêmeo de `decodeBase64` em
 * `server/api/bindings.ts`, pelo mesmo motivo que ele existe lá.
 */
export function encodeBase64(bytes: Uint8Array): string {
  let saida = '';

  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] ?? 0;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const trio = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);

    saida += BASE64_ALPHABET[(trio >> 18) & 63];
    saida += BASE64_ALPHABET[(trio >> 12) & 63];
    // O preenchimento não é enfeite: sem ele, o decodificador do outro lado não tem como
    // saber se o último grupo trazia um byte ou dois.
    saida += b === undefined ? '=' : BASE64_ALPHABET[(trio >> 6) & 63];
    saida += c === undefined ? '=' : BASE64_ALPHABET[trio & 63];
  }

  return saida;
}
